import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Search, Save, X, Mail } from "lucide-react";
import { HtmlPasteBox } from "../HtmlPasteBox";

const FALLBACK_COMPANIES = ["Glamour Pools and Spas", "Just Spas Newcastle"];

/* One editable employee record — legacy Form_Employee fields on the left,
   a personal HTML email signature per company on the right (3 companies). */
function EmployeeForm({ columns, pk, row, companies, onClose, onSaved }) {
  const isNew = !row;
  const [vals, setVals] = useState(() => {
    const base = {};
    columns.forEach((c) => { base[c] = row ? (row[c] ?? "") : ""; });
    return base;
  });
  const [sigs, setSigs] = useState(() => ({ ...((row && row.email_signatures) || {}) }));
  const [activeCo, setActiveCo] = useState(companies[0] || "");
  const [busy, setBusy] = useState(false);

  const scalarCols = columns.filter((c) => !["email_signatures", "Logo", "_id"].includes(c));
  const setSig = (co, html) => setSigs((s) => ({ ...s, [co]: html }));

  const save = async () => {
    if (!(String(vals[pk] || "").trim())) { toast.error(`${pk} is required`); return; }
    setBusy(true);
    try {
      const updated = { ...vals, email_signatures: sigs };
      if (isNew) await api("POST", `/config/tables/employee/row`, updated);
      else await api("POST", `/config/tables/employee/update`, { original: row, updated });
      toast.success(isNew ? "Employee added" : "Employee saved");
      onSaved();
    } catch (e) { toast.error(e.message || "Could not save employee"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" data-testid="employee-form">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[88vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 bg-[#123a63] text-white rounded-t-xl">
          <h3 className="font-bold">{isNew ? "Add Employee" : `Edit Employee — ${vals[pk] || ""}`}</h3>
          <button onClick={onClose} data-testid="employee-form-close" className="hover:bg-white/20 rounded p-1"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-4 overflow-auto grid md:grid-cols-2 gap-5">
          {/* LEFT — employee detail fields */}
          <div className="grid gap-3 content-start">
            <h4 className="text-[12px] font-black uppercase tracking-wide text-[#123a63]">Employee details</h4>
            {scalarCols.map((c) => (
              <label key={c} className="grid gap-1">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{c}{c === pk ? " (key)" : ""}</span>
                <input value={vals[c] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [c]: e.target.value }))}
                  data-testid={`employee-field-${c}`}
                  className="border border-slate-300 rounded px-2 py-1.5 text-sm focus:border-[#2f5fae] focus:ring-1 focus:ring-[#2f5fae] outline-none" />
              </label>
            ))}
          </div>

          {/* RIGHT — per-company HTML email signatures */}
          <div className="grid gap-2 content-start">
            <h4 className="text-[12px] font-black uppercase tracking-wide text-[#123a63] flex items-center gap-1.5"><Mail className="h-4 w-4" /> Email signatures (per company)</h4>
            <p className="text-[11px] text-slate-500 -mt-1">Copy this employee's signature in Outlook (select it all, Ctrl+C) and paste it below (Ctrl+V) — logos, links and colours are kept. It is attached automatically when they send mail from that company (not used on water-test / SMS messages).</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {companies.map((co) => (
                <button key={co} type="button" onClick={() => setActiveCo(co)} data-testid={`employee-sig-tab-${co}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${activeCo === co ? "bg-[#123a63] text-white border-[#123a63]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                  {co}{(sigs[co] || "").trim() ? " ●" : ""}
                </button>
              ))}
            </div>
            {activeCo && (
              <>
                <HtmlPasteBox key={activeCo} value={sigs[activeCo] || ""} onChange={(v) => setSig(activeCo, v)} testid="employee-sig-input" minH="min-h-[180px]" />
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 rounded border border-slate-300 text-sm font-semibold text-slate-600">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="employee-form-save" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded bg-[#123a63] text-white text-sm font-bold disabled:opacity-50"><Save className="h-4 w-4" /> Save</button>
        </div>
      </div>
    </div>
  );
}

export default function EmployeeEditor({ onBack, onExit }) {
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState(FALLBACK_COMPANIES);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit

  const load = useCallback(async (query = "") => {
    try { setData(await api("GET", `/config/tables/employee?q=${encodeURIComponent(query)}&limit=300`)); }
    catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api("GET", "/brand-profiles")
      .then((b) => { const names = (b || []).map((x) => x.name).filter(Boolean); if (names.length) setCompanies(names); })
      .catch(() => {});
  }, []);

  const del = async (row) => {
    if (!window.confirm(`Delete employee "${row[data.pk]}"? This cannot be undone.`)) return;
    try { await api("POST", `/config/tables/employee/delete`, { original: row }); toast.success("Employee deleted"); load(q); }
    catch (e) { toast.error(e.message); }
  };

  if (!data) return <p className="text-slate-500 py-10 text-center">Loading employees…</p>;
  const pk = data.pk;
  const allCols = data.columns || [];
  const preferred = [pk, "Company Name", "EmailAddress", "Telephone", "Mobile"];
  const listCols = preferred.filter((c) => c === pk || allCols.includes(c));
  const formCols = allCols.length ? allCols : preferred;
  const sigCount = (r) => Object.values((r && r.email_signatures) || {}).filter((v) => String(v || "").trim()).length;

  return (
    <div className="space-y-3" data-testid="employee-editor">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={onBack} data-testid="employee-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Toolbox</button>
        {onExit && <button onClick={onExit} data-testid="employee-switchboard" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border-l border-slate-200 pl-3">Switchboard</button>}
        <h2 className="font-display font-bold text-lg text-[#123a63]">Add / Edit Employee</h2>
        <span className="text-xs text-slate-400">{data.total} employees</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)}
              placeholder="Search…" data-testid="employee-search"
              className="pl-8 pr-2 py-1.5 border border-slate-300 rounded text-sm w-48 focus:border-[#2f5fae] outline-none" />
          </div>
          <button onClick={() => load(q)} className="px-3 py-1.5 rounded border border-slate-300 text-sm font-semibold text-slate-600">Search</button>
          <button onClick={() => setEditing(null)} data-testid="employee-add" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 text-white text-sm font-bold"><Plus className="h-4 w-4" /> Add Employee</button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-xl overflow-auto max-h-[calc(100vh-230px)]">
        <table className="w-full text-sm">
          <thead className="bg-[#123a63] text-white sticky top-0">
            <tr>
              {listCols.map((c) => <th key={c} className="text-left px-3 py-2 font-bold whitespace-nowrap">{c}</th>)}
              <th className="px-3 py-2 text-left font-bold whitespace-nowrap">Signatures</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} data-testid={`employee-row-${i}`} className="border-b border-slate-100 hover:bg-sky-50">
                {listCols.map((c) => <td key={c} className="px-3 py-1.5 whitespace-nowrap max-w-[260px] truncate" title={r[c]}>{r[c]}</td>)}
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${sigCount(r) ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{sigCount(r)}/{companies.length}</span>
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(r)} data-testid={`employee-edit-${i}`} className="text-[#2f5fae] hover:bg-sky-100 rounded p-1"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} data-testid={`employee-delete-${i}`} className="text-rose-600 hover:bg-rose-100 rounded p-1 ml-1"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={listCols.length + 2} className="text-center text-slate-400 py-8">No employees.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing !== undefined && (
        <EmployeeForm columns={formCols} pk={pk} row={editing} companies={companies}
          onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(q); }} />
      )}
    </div>
  );
}
